import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { formatDate } from '../../lib/ctoMembers';
import { knownSubject, type CtoRepoKnownRow } from '../../lib/ctoKnowns';
import { reviewSubject, summarizeRepos, type CtoReviewRow } from '../../lib/ctoReviews';
import { RepoMark } from './RepoMark';

type AccessStub = 'request' | 'join' | 'link';

const ACCESS_LABEL: Record<AccessStub, string> = {
  request: 'Request Access',
  join: 'Join',
  link: 'Link',
};

const ACCESS_NEXT: Record<AccessStub, AccessStub> = {
  request: 'join',
  join: 'link',
  link: 'request',
};

export function CtoRepoList({
  reviews,
  knowns = [],
  showPeople = true,
  onOpenReviews,
}: {
  reviews: CtoReviewRow[];
  knowns?: CtoRepoKnownRow[];
  showPeople?: boolean;
  onOpenReviews?: (repo: string) => void;
}) {
  const [access, setAccess] = useState<Record<string, AccessStub>>({});
  const repos = useMemo(() => summarizeRepos(reviews, knowns), [reviews, knowns]);
  const subjectsByRepo = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const review of reviews) {
      const subject = reviewSubject(review);
      const existing = map.get(review.repo) ?? [];
      if (!existing.includes(subject)) existing.push(subject);
      map.set(review.repo, existing);
    }
    for (const known of knowns) {
      const subject = knownSubject(known);
      const existing = map.get(known.repo) ?? [];
      if (!existing.includes(subject)) existing.push(subject);
      map.set(known.repo, existing);
    }
    return map;
  }, [reviews, knowns]);

  if (repos.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-16">
        No codebases yet. Store a review or a known and it will land here.
      </p>
    );
  }

  return (
    <ul className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 overflow-hidden">
      {repos.map((row) => {
        const stub = access[row.repo] ?? 'request';
        const people = subjectsByRepo.get(row.repo) ?? [];
        return (
          <li key={row.repo} className="px-4 py-3 flex items-center gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                <RepoMark repo={row.repo} />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {showPeople && people.length > 0 ? `${people.join(', ')} · ` : ''}
                {row.runs === 0
                  ? `known · last ${formatDate(row.lastKnownAt ?? row.lastReviewedAt)}`
                  : `${row.runs} ${row.runs === 1 ? 'run' : 'runs'} · ${row.openFindings} open · last ${formatDate(row.lastReviewedAt)}${row.hasKnown ? ' · known' : ''}`}
              </p>
            </div>
            {onOpenReviews && row.runs > 0 ? (
              <button
                type="button"
                onClick={() => onOpenReviews(row.repo)}
                className="text-xs text-fuchsia-600 dark:text-fuchsia-400 hover:underline shrink-0"
              >
                Reviews
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                toast.message('Stub — GitHub access is not wired yet.');
                setAccess((prev) => ({
                  ...prev,
                  [row.repo]: ACCESS_NEXT[prev[row.repo] ?? 'request'],
                }));
              }}
              className="shrink-0 text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {ACCESS_LABEL[stub]}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
